import { useMutation, useQuery } from "@tanstack/react-query";

import type { DeviceTokenApi } from "./device-token-api";
import {
  requestPermission,
  getToken,
  deleteToken,
  getDevicePlatform,
  unregisterDeviceForRemoteMessages,
  AUTHORIZATION_STATUS,
  getPermissionStatus,
} from "./messaging-service";
import type { PermissionStatus, PushNotificationStatus } from "./types";

export interface UseDeviceTokenOptions {
  deviceTokenApi: DeviceTokenApi;
  /** When true the status query is enabled. Typically gated on auth readiness. */
  enabled?: boolean;
}

export interface TeardownDeviceTokenOptions {
  /**
   * Known device token to unregister from backend. When omitted, teardown may
   * resolve the token via messaging depending on `lookupToken`.
   */
  token?: string;
  /**
   * When true, teardown tries `getToken()` if no token is provided.
   * Set false when disabling notifications without prompting/side effects.
   */
  lookupToken?: boolean;
}

export const STATUS_QUERY_KEY = ["push-notification-status"];

/**
 * Remove the device token from the backend, delete the local FCM token,
 * and unregister the device for remote messages. Each step runs
 * independently so a failure in one doesn't prevent the others.
 */
export async function teardownDeviceToken(
  deviceTokenApi: DeviceTokenApi,
  options: TeardownDeviceTokenOptions = {},
): Promise<void> {
  const { token: providedToken, lookupToken = true } = options;
  let token = providedToken;

  if (!token && lookupToken) {
    try {
      token = await getToken();
    } catch (error: unknown) {
      console.warn("[Push] Failed to resolve token for backend unregister:", error);
    }
  }

  if (token) {
    try {
      await deviceTokenApi.unregisterToken(token);
    } catch (error: unknown) {
      console.warn("[Push] Failed to unregister token from backend:", error);
    }
  }

  try {
    await deleteToken();
  } catch (error: unknown) {
    console.warn("[Push] Failed to delete local FCM token:", error);
  }

  try {
    await unregisterDeviceForRemoteMessages();
  } catch (error: unknown) {
    console.warn("[Push] Failed to unregister device for remote messages:", error);
  }
}

/**
 * React Query hook for all device-token operations: status query,
 * register mutation (request permission → get token → register),
 * and unregister mutation (unregister → delete local token).
 */
export function useDeviceToken({ deviceTokenApi, enabled = true }: UseDeviceTokenOptions) {
  const isRegistered = useQuery({
    queryKey: STATUS_QUERY_KEY,
    queryFn: async (): Promise<PushNotificationStatus> => {
      const status = await getPermissionStatus();

      if (status !== AUTHORIZATION_STATUS.AUTHORIZED && status !== AUTHORIZATION_STATUS.PROVISIONAL) {
        const permission: PermissionStatus = status === AUTHORIZATION_STATUS.DENIED ? "denied" : "undetermined";
        return { permission, registered: false };
      }

      const token = await getToken();
      const registered = await deviceTokenApi.isRegistered(token);
      return { permission: "granted", registered, token };
    },
    enabled,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const register = useMutation({
    mutationFn: async (): Promise<{ token: string }> => {
      const status = await requestPermission();
      if (status !== AUTHORIZATION_STATUS.AUTHORIZED && status !== AUTHORIZATION_STATUS.PROVISIONAL) {
        throw new PermissionDeniedError();
      }
      const token = await getToken();
      await deviceTokenApi.registerToken(token, getDevicePlatform());
      return { token };
    },
    onMutate: async (_, context) => {
      await context.client.cancelQueries({ queryKey: STATUS_QUERY_KEY });
      const previous = context.client.getQueryData<PushNotificationStatus>(STATUS_QUERY_KEY);
      context.client.setQueryData<PushNotificationStatus>(STATUS_QUERY_KEY, {
        permission: "granted",
        registered: true,
      });
      return { previous };
    },
    onError: (_, __, onMutateResult, context) => {
      context.client.setQueryData<PushNotificationStatus>(STATUS_QUERY_KEY, onMutateResult?.previous);
    },
    onSettled: (_, __, ___, ____, context) => {
      void context.client.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
    },
  });

  const unregister = useMutation({
    mutationFn: (options?: TeardownDeviceTokenOptions) => teardownDeviceToken(deviceTokenApi, options),
    onMutate: async (_, context) => {
      await context.client.cancelQueries({ queryKey: STATUS_QUERY_KEY });
      const previous = context.client.getQueryData<PushNotificationStatus>(STATUS_QUERY_KEY);
      context.client.setQueryData<PushNotificationStatus>(STATUS_QUERY_KEY, (old) => {
        if (!old) return { permission: "undetermined" as const, registered: false };
        return { ...old, registered: false };
      });
      return { previous };
    },
    onError: (_, __, onMutateResult, context) => {
      context.client.setQueryData<PushNotificationStatus>(STATUS_QUERY_KEY, onMutateResult?.previous);
    },
    onSettled: (_, __, ___, ____, context) => {
      void context.client.invalidateQueries({ queryKey: STATUS_QUERY_KEY });
    },
  });

  return { isRegistered, register, unregister };
}

export class PermissionDeniedError extends Error {
  constructor() {
    super("permission_denied");
    this.name = "PermissionDeniedError";
  }
}
